import React from "react";
import { elakayAutoText, elakayIntlLocale, elakayPluralText } from "../dataElakay";

// Presentation of the backend shipping estimate (useShippingEstimate). No
// shipping rule lives here: free/amount/days all come from the server.
// Customer view only: the amount, the delivery window and the packages. How
// the amount was determined (shipper, origin, lane, destination adjustment)
// is internal and is neither sent by the server nor shown here.

const LIVE_SOURCES = new Set(["live_rate", "carrier_rate", "supplier_live_quote"]);
// Estimator sources for which the order is refused server-side
// (elakay_services/shipping/estimator/rules.js BLOCKING_SOURCES): a CJ
// product the Seller disabled for, or never configured for, this country.
export const DESTINATION_BLOCKING_SOURCES = new Set(["destination_disabled", "destination_not_configured"]);
// rules.js RATE_BLOCKING_SOURCES: the real rate of this lane could not be
// obtained (supplier API down, no route) -- never replaced by an old rate.
export const RATE_BLOCKING_SOURCES = new Set(["supplier_rate_unavailable", "shipping_protection_failed"]);
export const SHIPPING_BLOCKING_SOURCES = new Set([...DESTINATION_BLOCKING_SOURCES, ...RATE_BLOCKING_SOURCES]);

// Customer-facing reason for a blocked line (never an internal/profit reason).
export const blockedLineMessage = (line, destinationCountry) => (
  RATE_BLOCKING_SOURCES.has(line?.cost?.source)
    ? elakayAutoText("h_salesim_rate_unavailable", "We could not calculate a valid shipping rate for this address.")
    : elakayAutoText("h_shipest_destination_blocked", "{product} is currently not available for delivery to {country}.", {
      product: line?.name || elakayAutoText("h_shipest_this_product", "This product"),
      country: countryLabel(destinationCountry) || elakayAutoText("h_shipest_this_country", "this country"),
    })
);

export const formatShippingMoney = (amount, currency = "USD") =>
  (Number(amount) || 0).toLocaleString("en-US", { style: "currency", currency: currency || "USD" });

export const countryLabel = (code) => {
  if (!code) return "";
  try {
    return new Intl.DisplayNames([elakayIntlLocale()].flat(), { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

export const businessDaysText = (min, max) => {
  if (!(max > 0)) return "";
  if (min === max) {
    return elakayPluralText(max, ["h_shipest_business_day_one", "{count} business day"], ["h_shipest_business_days", "{count} business days"]);
  }
  return elakayAutoText("h_shipest_business_days_range", "{min}–{max} business days", { min, max });
};

export const deliveryTypeText = (type) => {
  if (type === "regional") return elakayAutoText("h_shipest_regional", "Regional shipping");
  if (type === "national") return elakayAutoText("h_shipest_national", "National shipping");
  if (type === "international") return elakayAutoText("h_shipest_international", "International shipping");
  return "";
};

const formatDay = (isoDay) => {
  if (!isoDay) return "";
  const [y, m, d] = String(isoDay).split("-").map(Number);
  try {
    return new Intl.DateTimeFormat([elakayIntlLocale()].flat(), { month: "short", day: "numeric" }).format(new Date(y, m - 1, d));
  } catch {
    return isoDay;
  }
};

const Spinner = () => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      border: "2px solid #daeaf5", borderTopColor: "#007399",
      animation: "ck-spin 0.7s linear infinite", marginRight: 6, verticalAlign: "-1px",
    }}
  />
);

// Value of the "Shipping" row of the order summary.
const loadingText = (estimate) => (estimate?.updating
  ? elakayAutoText("h_salesim_updating", "Updating shipping…")
  : elakayAutoText("h_shipest_calculating", "Calculating shipping…"));

export const ShippingCostValue = ({ estimate }) => {
  const { status, data } = estimate || {};
  if (status === "loading" || status === "idle") {
    return (
      <span className="ck-summary__value" role="status" aria-live="polite" style={{ fontWeight: 500, fontSize: "0.82rem" }}>
        <Spinner />{loadingText(estimate)}
      </span>
    );
  }
  const totals = data?.totals;
  // A CJ product the Seller does not sell to this country: say so, never
  // "calculated at checkout" (the order would be refused).
  if (status === "ready" && (data?.lines || []).some((l) => SHIPPING_BLOCKING_SOURCES.has(l.cost?.source))) {
    const rateOnly = (data?.lines || []).every((l) => !DESTINATION_BLOCKING_SOURCES.has(l.cost?.source));
    return (
      <span className="ck-summary__value" style={{ fontWeight: 600, fontSize: "0.82rem", color: "#b42318" }}>
        {rateOnly
          ? elakayAutoText("h_salesim_rate_unavailable_short", "Shipping unavailable")
          : elakayAutoText("h_shipest_destination_blocked_reason", "Not available for this destination")}
      </span>
    );
  }
  // Some lines have a rate, others cannot be calculated yet: show what IS
  // added to the total, and say the rest comes later (never "free").
  if (status === "ready" && totals?.status === "unavailable" && Number(totals.chargeableAmount) > 0) {
    return (
      <span className="ck-summary__value" style={{ fontSize: "0.82rem" }}>
        {elakayAutoText("h_shipest_partial_amount", "{amount} + calculated at checkout", { amount: formatShippingMoney(totals.chargeableAmount, totals.currency) })}
      </span>
    );
  }
  if (status !== "ready" || !totals || totals.status === "unavailable") {
    return (
      <span className="ck-summary__value" style={{ fontWeight: 500, fontSize: "0.82rem" }}>
        {elakayAutoText("h_shipest_calculated_at_checkout", "Calculated at checkout")}
      </span>
    );
  }
  if (totals.status === "free") {
    return <span className="ck-summary__value ck-summary__value--free">{elakayAutoText("h_shipest_free", "Free Shipping")}</span>;
  }
  const live = (data.lines || []).some((l) => LIVE_SOURCES.has(l.cost?.source));
  const amount = formatShippingMoney(totals.chargeableAmount, totals.currency);
  return (
    <span className="ck-summary__value">
      {live ? elakayAutoText("h_shipest_estimated_amount", "Estimated {amount}", { amount }) : amount}
    </span>
  );
};

const GroupDetails = ({ group, index, count, showProducts }) => {
  const est = group.estimate || {};
  const deliveryType = deliveryTypeText(group.deliveryType);
  return (
    <div className="ck-ship-group" style={{ marginTop: index ? "0.6rem" : 0 }}>
      {count > 1 && (
        <div style={{ fontWeight: 600, color: "#004b63" }}>
          {elakayAutoText("h_shipest_package_n", "Package {index}", { index: index + 1 })}
          {showProducts && group.products?.length > 0 && (
            <span style={{ fontWeight: 400, color: "#5a7a8a" }}> · {group.products.map((p) => p.name).filter(Boolean).join(", ")}</span>
          )}
        </div>
      )}
      {est.status === "free" && count > 1 && (
        <div style={{ color: "#34c759", fontWeight: 600 }}>{elakayAutoText("h_shipest_free", "Free Shipping")}</div>
      )}
      {deliveryType && (
        <div style={{ color: "#5a7a8a" }}>
          {elakayAutoText("h_shipest_classification", "Delivery type")}: <strong>{deliveryType}</strong>
          {est.carrier ? <> · {elakayAutoText("h_shipest_via", "via {carrier}", { carrier: est.carrier })}</> : null}
        </div>
      )}
      {est.available ? (
        <div>
          {elakayAutoText("h_shipest_estimated_delivery", "Estimated delivery")}: <strong>{businessDaysText(est.minBusinessDays, est.maxBusinessDays)}</strong>
          {est.estimatedDeliveryFrom && (
            <span style={{ color: "#5a7a8a" }}>
              {" "}({elakayAutoText("h_shipest_arrives_between", "Arrives {from} – {to}", { from: formatDay(est.estimatedDeliveryFrom), to: formatDay(est.estimatedDeliveryTo) })})
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Detailed shipping note under the totals: lane, fulfillment, delivery window,
// and the honest fallbacks (address missing / not calculable / failed).
// `addressNotSelected`: the estimate was made for the visitor's approximate
// (IP) location, not a delivery address -- still ask for one.
export const ShippingEstimateDetails = ({ estimate, addressNotSelected = false }) => {
  const { status, data } = estimate || {};
  if (status === "loading" || status === "idle") {
    return (
      <div className="ck-summary__shipping-note" role="status" aria-live="polite">
        <Spinner />{loadingText(estimate)}
      </div>
    );
  }
  if (status !== "ready" || !data) {
    return (
      <div className="ck-summary__shipping-note">
        {elakayAutoText("h_shipest_will_be_calculated", "Shipping will be calculated at checkout.")}
      </div>
    );
  }
  const groups = data.groups || [];
  const destinationMissing = data.destination?.status !== "resolved";
  const includedInPrice = (data.lines || []).length > 0 && data.lines.every((l) => l.cost?.freeReason === "included_in_price");
  const destinationCountry = data.destination?.country || "";
  const blockedLines = (data.lines || []).filter((l) => SHIPPING_BLOCKING_SOURCES.has(l.cost?.source));
  return (
    <div className="ck-summary__shipping-note">
      {groups.length > 1 && (
        <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>
          {elakayAutoText("h_shipest_packages", "Ships in {count} packages", { count: groups.length })}
        </div>
      )}
      {groups.map((group, i) => (
        <GroupDetails key={group.groupKey} group={group} index={i} count={groups.length} showProducts={groups.length > 1} />
      ))}
      {includedInPrice && data.totals?.status === "free" && (
        <div style={{ color: "#5a7a8a" }}>{elakayAutoText("h_shipest_included_in_price", "Shipping included in price")}</div>
      )}
      {!destinationMissing && blockedLines.map((l) => (
        <div key={`blocked-${l.index}`} className="ck-note ck-note--warn" role="alert" style={{ marginTop: "0.35rem" }}>
          {blockedLineMessage(l, destinationCountry)}
        </div>
      ))}
      {(destinationMissing || addressNotSelected) && (
        <div className="ck-note ck-note--warn" style={{ marginTop: "0.35rem" }}>
          {elakayAutoText("h_shipest_select_address", "Select a delivery address to estimate shipping.")}
        </div>
      )}
      {!destinationMissing && data.totals?.status === "unavailable" && !blockedLines.length && (
        <div style={{ marginTop: "0.35rem" }}>
          {elakayAutoText("h_shipest_will_be_calculated", "Shipping will be calculated at checkout.")}{" "}
          {elakayAutoText("h_shipest_not_in_total", "Shipping is not included in this total yet.")}
        </div>
      )}
    </div>
  );
};
